import i18next from 'i18next';

import { PRICE_NUMBER_DECIMALS } from '@commons/DisplayNumber';
import utilsXLS from '@commons/utils/makeXLS';

import { CATALOG_VAT_RATE_DECIMALS } from './getCatalogProductColumns';

const getColumnsSettings = () => [
  {
    propertyKey: 'name',
    name: i18next.t('EXTERNAL_CLIENTS.LIST.COLUMN_CLIENT'),
  },
  {
    propertyKey: 'externalReference',
    name: i18next.t('EXTERNAL_CLIENTS.LIST.COLUMN_EXTERNAL_REFERENCE'),
  },
  {
    propertyKey: 'stores',
    name: i18next.t('EXTERNAL_CLIENTS.LIST.COLUMN_STORES'),
    transform: (stores) => (stores ?? []).map(({ name }) => name).join(', '),
  },
  {
    propertyKey: 'productCount',
    name: i18next.t('EXTERNAL_CLIENTS.LIST.COLUMN_PRODUCT_COUNT'),
  },
];

/**
 * Client-side XLSX generation of the external clients list
 * @param {Object[]} externalClients - The external clients to write in the file
 *
 * @returns {void}
 */
export const exportExternalClients = (externalClients) => {
  const sheet = utilsXLS.generateDefaultSheet(
    i18next.t('FEATURE.EXTERNAL.CLIENTS'),
    getColumnsSettings(),
    externalClients,
  );

  utilsXLS.makeXLS(i18next.t('EXTERNAL_CLIENTS.LIST.EXPORT_FILENAME'), [sheet]);
};

/**
 * Columns of the catalog export, mirroring the ones of the catalog view (getCatalogProductColumns):
 *
 * @param {boolean} hasCentralKitchens - Whether the client owns at least one central kitchen
 *
 * @returns {Array<Object>} The sheet columns settings
 */
const getCatalogColumnsSettings = (hasCentralKitchens) => {
  const columns = [
    {
      propertyKey: 'name',
      name: i18next.t('GENERAL.PRODUCT'),
    },
    {
      propertyKey: 'sku',
      name: i18next.t('GENERAL.SKU'),
    },
    {
      propertyKey: 'category',
      name: i18next.t('GENERAL.CATEGORY'),
      transform: (category) => category || i18next.t('GENERAL.NONE_VALUE'),
    },
    {
      propertyKey: 'subCategory',
      name: i18next.t('GENERAL.SUB_CATEGORY'),
      transform: (subCategory) => subCategory || i18next.t('GENERAL.NONE_VALUE'),
    },
  ];

  if (hasCentralKitchens) {
    columns.push({
      propertyKey: 'isKitchen',
      name: i18next.t('EXTERNAL_CLIENTS.CREATION.MANUAL_COLUMN_CENTRAL_PRODUCT'),
      transform: (isKitchen) => i18next.t(isKitchen ? 'GENERAL.YES' : 'GENERAL.NO'),
    });
  }

  columns.push(
    {
      type: 'currency',
      propertyKey: 'priceWithTaxes',
      name: i18next.t('EXTERNAL_CLIENTS.CREATION.MANUAL_COLUMN_PRICE'),
      decimals: PRICE_NUMBER_DECIMALS,
    },
    {
      type: 'percentage',
      propertyKey: 'vatRate',
      name: i18next.t('GENERAL.VAT_RATE'),
      decimals: CATALOG_VAT_RATE_DECIMALS,
    },
  );

  return columns;
};

/**
 * Client-side XLSX generation of an external client product catalog
 *
 * @param {Object[]} catalogProducts - The catalog products to write in the file
 * @param {Object} params
 * @param {string} params.clientName - Name of the client owning the catalog, used in the file name
 * @param {boolean} params.hasCentralKitchens - Whether the client owns at least one central kitchen
 * @param {Object} params.currency - Currency of the client, appended next to the prices
 *
 * @returns {void}
 */
export const exportExternalClientCatalog = (
  catalogProducts,
  { clientName, hasCentralKitchens, currency },
) => {
  const sheet = utilsXLS.generateDefaultSheet(
    i18next.t('EXTERNAL_CLIENTS.DETAILS.CATALOG_TAB'),
    getCatalogColumnsSettings(hasCentralKitchens),
    catalogProducts,
    currency,
  );

  utilsXLS.makeXLS(i18next.t('EXTERNAL_CLIENTS.CATALOG.EXPORT_FILENAME', { clientName }), [sheet]);
};
